import React from 'react';
import { ShieldCheck, Cloud, BrainCircuit, Code2, Terminal, CheckCircle, ArrowRight } from 'lucide-react';
import CompanyLogos from '../components/CompanyLogos';
import { Link } from 'react-router-dom';

const CloudDevOpsProgram = () => {
    return (
        <div className="min-h-screen bg-brand-bg">
            {/* Hero Section */}
            <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-brand-textLight py-20 px-6 sm:px-12 lg:px-24">
                <div className="max-w-7xl mx-auto">
                    <div className="md:w-3/4 lg:w-2/3">
                        <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                            Cloud and DevOps Training Program
                        </h1>
                        <p className="text-xl text-brand-textMuted mb-8 max-w-2xl">
                            Equipping professionals with DevSecOps and Generative AI skills on Google Cloud Platform to build, secure, and innovate at scale.
                        </p>
                        <Link
                            to="/contact-us"
                            className="inline-flex items-center px-8 py-4 bg-brand-gold text-black font-bold rounded-lg shadow-lg hover:bg-brand-goldHover transition-transform transform hover:-translate-y-1"
                        >
                            Enroll Now
                            <ArrowRight className="ml-2 h-5 w-5" />
                        </Link>
                    </div>
                </div>
            </div>

            {/* Scrolling Company Logos Section */}
            <CompanyLogos />

            {/* Main Content */}
            <div className="max-w-7xl mx-auto py-16 px-6 sm:px-12 lg:px-24">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">

                    {/* Program Overview */}
                    <div className="lg:col-span-2 space-y-8">
                        <div className="bg-brand-card p-8 rounded-2xl shadow-sm border border-gray-800">
                            <h2 className="text-2xl font-bold text-brand-textLight mb-6 flex items-center">
                                <Terminal className="text-brand-gold mr-3" size={28} />
                                Program Overview
                            </h2>
                            <p className="text-brand-textMuted leading-relaxed mb-6">
                                This comprehensive Cloud and DevOps training program combines DevSecOps and Generative AI on Google Cloud Platform to equip professionals with the skills needed to build, secure, and innovate at scale. It focuses on integrating security seamlessly into the collaboration between development and operations teams, enabling security officers and DevOps engineers to proactively protect cloud-native applications throughout the CI/CD pipeline.
                            </p>
                            <p className="text-brand-textMuted leading-relaxed">
                                At the same time, it delivers an intensive, hands-on exploration of Generative AI within the GCP ecosystem—covering core concepts, models, tools, and real-world use cases—so learners can design, deploy, and manage AI-powered solutions responsibly and efficiently. Together, the program empowers organizations to achieve secure, resilient DevOps practices while leveraging cutting-edge cloud and AI technologies to drive innovation.
                            </p>
                        </div>

                        {/* Key Modules Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-brand-card p-6 rounded-xl shadow-sm border border-gray-800 hover:shadow-md transition-shadow">
                                <ShieldCheck className="text-indigo-600 mb-4 h-8 w-8" />
                                <h3 className="text-lg font-bold text-brand-textLight mb-2">DevSecOps Integration</h3>
                                <p className="text-sm text-brand-textMuted">Integrate security seamlessly into development and operations throughout the CI/CD pipeline.</p>
                            </div>

                            <div className="bg-brand-card p-6 rounded-xl shadow-sm border border-gray-800 hover:shadow-md transition-shadow">
                                <Cloud className="text-blue-500 mb-4 h-8 w-8" />
                                <h3 className="text-lg font-bold text-brand-textLight mb-2">Google Cloud Platform</h3>
                                <p className="text-sm text-brand-textMuted">Master the GCP ecosystem for scalable, resilient, and secure application deployment.</p>
                            </div>

                            <div className="bg-brand-card p-6 rounded-xl shadow-sm border border-gray-800 hover:shadow-md transition-shadow">
                                <BrainCircuit className="text-purple-600 mb-4 h-8 w-8" />
                                <h3 className="text-lg font-bold text-brand-textLight mb-2">Generative AI</h3>
                                <p className="text-sm text-brand-textMuted">Explore core concepts, models, and real-world use cases within the GCP ecosystem.</p>
                            </div>

                            <div className="bg-brand-card p-6 rounded-xl shadow-sm border border-gray-800 hover:shadow-md transition-shadow">
                                <Code2 className="text-green-500 mb-4 h-8 w-8" />
                                <h3 className="text-lg font-bold text-brand-textLight mb-2">Hands-on Experience</h3>
                                <p className="text-sm text-brand-textMuted">Intensive practical training to design, deploy, and manage solutions efficiently.</p>
                            </div>
                        </div>
                    </div>

                    {/* Sidebar / CTA */}
                    <div className="lg:col-span-1">
                        <div className="bg-brand-card text-brand-textLight p-8 rounded-2xl shadow-xl sticky top-24">
                            <h3 className="text-2xl font-bold mb-6">Why Join This Program?</h3>
                            <ul className="space-y-4 mb-8">
                                {[
                                    "Master DevSecOps on GCP",
                                    "Leverage Generative AI",
                                    "Secure CI/CD Pipelines",
                                    "Build Cloud-Native Apps",
                                    "Drive Innovation at Scale"
                                ].map((item, index) => (
                                    <li key={index} className="flex items-start">
                                        <CheckCircle className="text-brand-gold mr-3 mt-1 flex-shrink-0" size={18} />
                                        <span className="text-brand-textMuted">{item}</span>
                                    </li>
                                ))}
                            </ul>
                            <div className="space-y-4">
                                <button className="w-full py-3 bg-brand-gold hover:bg-brand-goldHover text-black font-bold rounded-lg transition-colors">
                                    Download Syllabus
                                </button>
                                <Link to="/contact-us" className="block w-full py-3 border border-brand-textMuted text-center text-brand-textMuted hover:text-black font-semibold rounded-lg transition-colors">
                                    Contact Admissions
                                </Link>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default CloudDevOpsProgram;
